import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import type { ClientViewMapPoint, InspectorLocation } from '../../api/types';
import {
  PRIORITY_ORDER, PRIORITY_LABEL, PRIORITY_COLOR,
  TIER_ORDER, TIER_LABEL, TIER_COLOR,
  type PriorityKey,
  PRIORITY_INDEX, pointPriorityCount, pointDominantPriority, tierKeyFromIndex,
} from './clientViewTokens';
import type { OutFeature, LeafFeature } from './clusterWorker';
import { spiderfyPositions } from '../common/spiderfyLayout';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon issue with bundlers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const inspectorIcon = new L.DivIcon({
  className: 'inspector-marker',
  html: `<div style="
    background: #005BA8;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 2px 8px rgba(0, 91, 168,0.6);
  "></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  popupAnchor: [0, -14],
});

// Clients in the same ZIP resolve to one shared centroid, so their points would
// stack exactly on top of each other. ~66m minimum spacing keeps co-located
// pins individually visible/clickable; the ring radius grows with the group
// size so even large same-ZIP groups don't re-overlap.
const MIN_SEP_DEG = 0.0006;

// Above this a cluster zooms to its own split point instead of fanning out, so
// clicking walks it down in steps. The exception is a cluster sitting entirely
// on one ZIP centroid, which no zoom can separate — that always fans out.
const SPIDERFY_MAX = 50;

/**
 * Fan co-located leaves out onto a small ring around their shared centroid.
 * Only individual (non-cluster) points are passed here, so this runs over at
 * most a few hundred features per viewport.
 */
const fanOutLeaves = (
  leaves: { sellerId: string; lat: number; lng: number }[],
): Map<string, [number, number]> => {
  const groups = new Map<string, { sellerId: string; lat: number; lng: number }[]>();
  for (const l of leaves) {
    const key = `${l.lat.toFixed(5)},${l.lng.toFixed(5)}`;
    const g = groups.get(key);
    if (g) g.push(l);
    else groups.set(key, [l]);
  }
  const plot = new Map<string, [number, number]>();
  for (const g of groups.values()) {
    if (g.length === 1) {
      plot.set(g[0].sellerId, [g[0].lat, g[0].lng]);
      continue;
    }
    const ringR = MIN_SEP_DEG / (2 * Math.sin(Math.PI / g.length));
    g.forEach((l, i) => {
      const angle = (2 * Math.PI * i) / g.length;
      const offLat = l.lat + ringR * Math.sin(angle);
      // Keep the ring circular by scaling the east-west offset by cos(lat).
      const offLng = l.lng + (ringR * Math.cos(angle)) / Math.cos((l.lat * Math.PI) / 180);
      plot.set(l.sellerId, [offLat, offLng]);
    });
  }
  return plot;
};

/**
 * Build popup HTML from a thin map point. The thin payload has no last-contact
 * fields (those live on the batch-fetched card body the carousel renders), so
 * the popup shows identity, location, distance, the priority breakdown and the
 * client tier only.
 */
const buildPopupHtml = (p: ClientViewMapPoint): string => {
  const distanceHtml = p.distance_miles != null
    ? `<div class="cv-popup-distance">${p.distance_miles} mi away</div>`
    : '';
  const locationParts = [p.territory_city, p.territory_state].filter(Boolean).join(', ');
  const locationHtml = locationParts ? `<div class="cv-popup-location">${locationParts}</div>` : '';
  const priorityLines = PRIORITY_ORDER
    .filter((k) => pointPriorityCount(p, k) > 0)
    .map((k) => `<div><span class="cv-popup-swatch" style="background:${PRIORITY_COLOR[k].pin}"></span><strong>${pointPriorityCount(p, k)}</strong> ${PRIORITY_LABEL[k]}</div>`)
    .join('');
  const tier = tierKeyFromIndex(p.tier);
  const tierLabel = TIER_LABEL[tier];
  const tierColor = TIER_COLOR[tier];
  return `
    <div class="cv-popup">
      <div class="cv-popup-header">
        <div class="cv-popup-name">${p.seller_name || `Dealer ${p.seller_id}`}</div>
        <span class="cv-popup-tier" style="background:${tierColor}">${tierLabel}</span>
      </div>
      <div class="cv-popup-id">${p.seller_id}</div>
      ${locationHtml}
      ${distanceHtml}
      ${priorityLines ? `<hr class="cv-popup-divider"/><div class="cv-popup-priorities">${priorityLines}</div>` : ''}
    </div>
  `;
};

interface ClientViewMapProps {
  points: ClientViewMapPoint[];
  inspectorLocation: InspectorLocation | null;
  radiusMiles: number | null;
  isInspectorMode: boolean;
  loading: boolean;
  selectedDealerId?: string | null;
  /** Fired when the user clicks a dealer marker; pass null to unfocus (background click). */
  onDealerClick?: (sellerId: string | null) => void;
}

const MapBoundsUpdater: React.FC<{
  points: ClientViewMapPoint[];
  inspectorLocation: InspectorLocation | null;
  radiusMiles: number | null;
}> = ({ points, inspectorLocation, radiusMiles }) => {
  const map = useMap();
  const prevBoundsKey = useRef('');

  useEffect(() => {
    const boundsKey = `${inspectorLocation?.zip_code || ''}-${radiusMiles || ''}-${points.length}-${points[0]?.seller_id || ''}`;
    if (boundsKey === prevBoundsKey.current) return;
    prevBoundsKey.current = boundsKey;

    if (inspectorLocation && radiusMiles) {
      const radiusMeters = radiusMiles * 1609.34;
      const center = L.latLng(inspectorLocation.latitude, inspectorLocation.longitude);
      const bounds = center.toBounds(radiusMeters * 2);
      map.fitBounds(bounds, { padding: [30, 30] });
    } else {
      const coords = points
        .filter((d) => d.latitude != null && d.longitude != null)
        .map((d) => [d.latitude as number, d.longitude as number] as [number, number]);
      if (coords.length > 0) {
        const bounds = L.latLngBounds(coords);
        map.fitBounds(bounds, { padding: [40, 40] });
      } else {
        map.setView([39.8283, -98.5795], 4);
      }
    }
  }, [points, inspectorLocation, radiusMiles, map]);

  return null;
};

/**
 * Renders the dealer layer using supercluster (in a Web Worker) for clustering
 * and a shared Leaflet canvas renderer for the individual points.
 *
 * - The thin points are handed to the worker once per data change.
 * - On every pan/zoom we ask the worker for the clusters + leaves inside the
 *   current viewport (a few hundred features at most) and rebuild the layer.
 * - Clusters are DivIcon markers tinted by their hottest dealer; individual
 *   dealers are L.circleMarker on one L.canvas() renderer (fill = dominant
 *   priority, stroke = client tier) so thousands draw without DOM overhead.
 */
const SuperclusterCanvasLayer: React.FC<{
  points: ClientViewMapPoint[];
  selectedDealerId?: string | null;
  onDealerClick?: (sellerId: string | null) => void;
}> = ({ points, selectedDealerId, onDealerClick }) => {
  const map = useMap();
  const workerRef = useRef<Worker | null>(null);
  const rendererRef = useRef<L.Canvas | null>(null);
  const clusterLayerRef = useRef<L.LayerGroup | null>(null);
  const pointLayerRef = useRef<L.LayerGroup | null>(null);
  const spiderLayerRef = useRef<L.LayerGroup | null>(null);
  const spiderClusterRef = useRef<number | null>(null);
  const circleByIdRef = useRef<Map<string, L.CircleMarker>>(new Map());
  const pointByIdRef = useRef<Map<string, ClientViewMapPoint>>(new Map());
  const reqIdRef = useRef(0);
  const loadedRef = useRef(false);
  const pendingOpenRef = useRef<string | null>(null);

  const onClickRef = useRef(onDealerClick);
  onClickRef.current = onDealerClick;
  const selectedRef = useRef<string | null | undefined>(selectedDealerId);
  selectedRef.current = selectedDealerId;

  // ----- One dealer dot, shared by the viewport render and the spiderfy layer -----
  const makeLeafCircle = useCallback((f: LeafFeature, lat: number, lng: number): L.CircleMarker => {
    const dominant: PriorityKey = PRIORITY_ORDER[f.pri] ?? 'other';
    const fill = PRIORITY_COLOR[dominant].pin;
    const ring = TIER_COLOR[tierKeyFromIndex(f.tier)];
    const isSelected = f.sellerId === selectedRef.current;
    const circle = L.circleMarker([lat, lng], {
      renderer: rendererRef.current ?? undefined,
      radius: isSelected ? 9 : 6,
      fillColor: fill,
      fillOpacity: 1,
      color: ring,
      weight: isSelected ? 4 : 3,
    });
    const point = pointByIdRef.current.get(f.sellerId);
    if (point) {
      circle.bindPopup(() => buildPopupHtml(point), {
        maxWidth: 280,
        // Hover-driven card: don't pan the map when it opens (jarring while
        // moving the cursor over points) and skip the close button since
        // mouseout handles dismissal.
        autoPan: false,
        closeButton: false,
      });
    }
    // Open the basic-detail card on hover instead of requiring a click.
    circle.on('mouseover', () => circle.openPopup());
    circle.on('mouseout', () => circle.closePopup());
    circle.on('click', (ev) => {
      L.DomEvent.stopPropagation(ev);
      onClickRef.current?.(f.sellerId);
    });
    return circle;
  }, []);

  const clearSpider = useCallback(() => {
    spiderLayerRef.current?.clearLayers();
    spiderClusterRef.current = null;
  }, []);

  /**
   * Fan a cluster's dealers out around its centre, each on a leg back to the
   * shared plot. Dealers in one ZIP collapse to a single centroid, so there is
   * no geographic spread to zoom into — without this they only separate at max
   * zoom, if at all.
   */
  const spiderfy = useCallback((clusterId: number, lat: number, lng: number, leaves: LeafFeature[]) => {
    const layer = spiderLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    const centre = L.latLng(lat, lng);
    const positions = spiderfyPositions(map, centre, leaves.length);
    leaves.forEach((leaf, i) => {
      const pos = positions[i];
      layer.addLayer(L.polyline([centre, pos], {
        color: '#94a3b8', weight: 1.5, opacity: 0.7, interactive: false,
      }));
      layer.addLayer(makeLeafCircle(leaf, pos.lat, pos.lng));
    });
    spiderClusterRef.current = clusterId;
  }, [map, makeLeafCircle]);

  // ----- Render the worker's viewport features -----
  const render = useCallback((features: OutFeature[]) => {
    const clusterLayer = clusterLayerRef.current;
    const pointLayer = pointLayerRef.current;
    const renderer = rendererRef.current;
    if (!clusterLayer || !pointLayer || !renderer) return;

    clusterLayer.clearLayers();
    pointLayer.clearLayers();
    circleByIdRef.current.clear();

    const leaves = features.filter((f): f is Extract<OutFeature, { cluster: false }> => !f.cluster);
    const plot = fanOutLeaves(leaves.map((l) => ({ sellerId: l.sellerId, lat: l.lat, lng: l.lng })));

    for (const f of features) {
      if (f.cluster) {
        const dominant: PriorityKey = PRIORITY_ORDER[f.minPri] ?? 'other';
        const bg = PRIORITY_COLOR[dominant].pin;
        const size = f.count >= 50 ? 'large' : f.count >= 10 ? 'medium' : 'small';
        const icon = L.divIcon({
          html: `<div class="cv-cluster cv-cluster-${size}" style="background:${bg}"><span>${f.count}</span></div>`,
          className: 'cv-cluster-wrapper',
          iconSize: L.point(36, 36),
        });
        const marker = L.marker([f.lat, f.lng], { icon });
        marker.on('click', (ev) => {
          L.DomEvent.stopPropagation(ev);
          if (spiderClusterRef.current === f.id) { clearSpider(); return; }
          clearSpider();
          // The worker decides: fan out its dealers, or tell us the zoom the
          // cluster actually splits at (never just "current + 2").
          workerRef.current?.postMessage({
            type: 'expand', clusterId: f.id, lat: f.lat, lng: f.lng, limit: SPIDERFY_MAX,
          });
        });
        clusterLayer.addLayer(marker);
      } else {
        const [lat, lng] = plot.get(f.sellerId) ?? [f.lat, f.lng];
        const circle = makeLeafCircle(f, lat, lng);
        pointLayer.addLayer(circle);
        circleByIdRef.current.set(f.sellerId, circle);

        // The selection effect may have asked us to open this dealer's popup
        // once it became an individually-rendered leaf (e.g. after zooming in
        // far enough to break its cluster).
        if (pendingOpenRef.current === f.sellerId) {
          circle.openPopup();
          pendingOpenRef.current = null;
        }
      }
    }
  }, [makeLeafCircle, clearSpider]);

  // ----- Query the worker for the current viewport -----
  const query = useCallback(() => {
    if (!loadedRef.current || !workerRef.current) return;
    const b = map.getBounds();
    const bbox: [number, number, number, number] = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
    const id = ++reqIdRef.current;
    workerRef.current.postMessage({ type: 'query', id, bbox, zoom: Math.round(map.getZoom()) });
  }, [map]);

  // ----- One-time setup: renderer, layers, worker, map handlers -----
  useEffect(() => {
    const renderer = L.canvas({ padding: 0.5 });
    rendererRef.current = renderer;
    const clusterLayer = L.layerGroup().addTo(map);
    const pointLayer = L.layerGroup().addTo(map);
    const spiderLayer = L.layerGroup().addTo(map);
    const circleById = circleByIdRef.current;
    clusterLayerRef.current = clusterLayer;
    pointLayerRef.current = pointLayer;
    spiderLayerRef.current = spiderLayer;

    const worker = new Worker(new URL('./clusterWorker.ts', import.meta.url), { type: 'module' });
    workerRef.current = worker;
    worker.onmessage = (event: MessageEvent) => {
      const msg = event.data;
      if (msg.type === 'loaded') {
        loadedRef.current = true;
        query();
      } else if (msg.type === 'result') {
        if (msg.id !== reqIdRef.current) return; // drop stale viewport responses
        render(msg.features as OutFeature[]);
      } else if (msg.type === 'expanded') {
        if (msg.leaves.length > 0) {
          spiderfy(msg.clusterId, msg.lat, msg.lng, msg.leaves as LeafFeature[]);
        } else {
          map.setView([msg.lat, msg.lng], Math.min(msg.expansionZoom, map.getMaxZoom()));
        }
      }
    };

    const handleBackgroundClick = () => {
      clearSpider();
      onClickRef.current?.(null);
    };
    map.on('moveend', query);
    // A fan-out is anchored to one screen position, so it can't survive a move.
    map.on('movestart', clearSpider);
    map.on('click', handleBackgroundClick);

    return () => {
      map.off('moveend', query);
      map.off('movestart', clearSpider);
      map.off('click', handleBackgroundClick);
      worker.terminate();
      workerRef.current = null;
      map.removeLayer(clusterLayer);
      map.removeLayer(pointLayer);
      map.removeLayer(spiderLayer);
      clusterLayerRef.current = null;
      pointLayerRef.current = null;
      spiderLayerRef.current = null;
      circleById.clear();
    };
  }, [map, query, render, spiderfy, clearSpider]);

  // ----- (Re)load the worker index whenever the point set changes -----
  useEffect(() => {
    pointByIdRef.current = new Map(points.map((p) => [p.seller_id, p]));
    loadedRef.current = false;
    const payload = points
      .filter((p) => p.latitude != null && p.longitude != null)
      .map((p) => ({
        id: p.seller_id,
        lng: p.longitude as number,
        lat: p.latitude as number,
        pri: PRIORITY_INDEX[pointDominantPriority(p)],
        tier: p.tier,
      }));
    workerRef.current?.postMessage({ type: 'load', points: payload });
  }, [points]);

  // ----- Selection: highlight, pan to, and open the selected dealer -----
  useEffect(() => {
    pendingOpenRef.current = selectedDealerId ?? null;
    if (selectedDealerId) {
      const point = pointByIdRef.current.get(selectedDealerId);
      if (point && point.latitude != null && point.longitude != null) {
        const zoom = map.getZoom();
        // Zoom in far enough to break the dealer out of its cluster so the
        // individual pin (and its popup) becomes visible.
        map.setView([point.latitude, point.longitude], zoom < 14 ? 14 : zoom);
      }
    }
    // Re-render so the highlight (and any popup) updates even when the view
    // didn't move. A stale-id guard drops the redundant query if a moveend
    // from the setView above also fires.
    query();
  }, [selectedDealerId, map, query]);

  return null;
};

/**
 * Calls `map.invalidateSize()` whenever the map's container element resizes
 * (e.g. when the user drags the carousel/map splitter). Without this,
 * Leaflet keeps the previous tile size and renders blank space.
 */
const MapResizeObserver: React.FC = () => {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    if (!container || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      // rAF avoids layout thrash mid-drag
      window.requestAnimationFrame(() => map.invalidateSize());
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, [map]);
  return null;
};

/**
 * Tracks the effective theme (light/dark) following the data-theme attribute
 * and the system color-scheme preference when no explicit theme is set.
 */

export const ClientViewMap: React.FC<ClientViewMapProps> = React.memo(({
  points,
  inspectorLocation,
  radiusMiles,
  loading,
  selectedDealerId,
  onDealerClick,
}) => {
  const stablePoints = useMemo(() => points, [points]);

  // Legend visibility — persisted so each user's show/hide preference sticks.
  const [legendHidden, setLegendHidden] = useState<boolean>(
    () => localStorage.getItem('cv-map-legend-hidden') === 'true',
  );
  const toggleLegend = useCallback(() => {
    setLegendHidden((prev) => {
      const next = !prev;
      localStorage.setItem('cv-map-legend-hidden', String(next));
      return next;
    });
  }, []);

  return (
    <div className="cv-map-wrapper">
      {loading && (
        <div className="cv-map-loading">
          <span>Loading dealers...</span>
        </div>
      )}
      <MapContainer
        center={[39.8283, -98.5795]}
        zoom={4}
        className="cv-map"
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <MapBoundsUpdater
          points={stablePoints}
          inspectorLocation={inspectorLocation}
          radiusMiles={radiusMiles}
        />
        <MapResizeObserver />

        {inspectorLocation && (
          <>
            <Marker
              position={[inspectorLocation.latitude, inspectorLocation.longitude]}
              icon={inspectorIcon}
            >
              <Popup>
                <div className="cv-popup">
                  <div className="cv-popup-name">
                    {inspectorLocation.seller_name || 'Inspector Location'}
                  </div>
                  <div className="cv-popup-id">
                    {inspectorLocation.seller_id && `${inspectorLocation.seller_id} · `}
                    {inspectorLocation.city}{inspectorLocation.city && inspectorLocation.state ? ', ' : ''}{inspectorLocation.state} {inspectorLocation.zip_code}
                  </div>
                </div>
              </Popup>
            </Marker>
            {radiusMiles && (
              <Circle
                center={[inspectorLocation.latitude, inspectorLocation.longitude]}
                radius={radiusMiles * 1609.34}
                // Non-interactive so the filled overlay doesn't swallow hover/click
                // events meant for the dealer dots rendered underneath it.
                interactive={false}
                pathOptions={{
                  color: '#005BA8',
                  fillColor: '#005BA8',
                  fillOpacity: 0.08,
                  weight: 2,
                  dashArray: '6 4',
                  interactive: false,
                }}
              />
            )}
          </>
        )}

        <SuperclusterCanvasLayer
          points={stablePoints}
          selectedDealerId={selectedDealerId}
          onDealerClick={onDealerClick}
        />
      </MapContainer>

      {/* Legend — two scales, collapsible */}
      <div className={`cv-map-legend${legendHidden ? ' cv-map-legend-hidden' : ''}`}>
        <button
          type="button"
          className="cv-legend-toggle"
          onClick={toggleLegend}
          title={legendHidden ? 'Show legend' : 'Hide legend'}
          aria-label={legendHidden ? 'Show legend' : 'Hide legend'}
          aria-expanded={!legendHidden}
        >
          {legendHidden ? 'Legend' : '✕'}
        </button>
        {!legendHidden && (
          <>
            <div className="cv-legend-section">
              <div className="cv-legend-title">Priority (center)</div>
              {PRIORITY_ORDER.map((k) => (
                <div key={k} className="cv-legend-item">
                  <span className="cv-legend-dot" style={{ background: PRIORITY_COLOR[k].pin }}></span>
                  {PRIORITY_LABEL[k]}
                </div>
              ))}
            </div>
            <div className="cv-legend-section">
              <div className="cv-legend-title">Client Tier (ring)</div>
              {TIER_ORDER.map((k) => (
                <div key={k} className="cv-legend-item">
                  <span className="cv-legend-ring" style={{ borderColor: TIER_COLOR[k] }}></span>
                  {TIER_LABEL[k]}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
});
